import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, KeyRound } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import type { User, Tenant } from '@wsfull/shared'

type UserForm = { name: string; email: string; password: string; role: string; tenantId: string }
type EditForm = { name: string; role: string; active: boolean }
type PwdForm  = { newPassword: string }

const emptyCreate: UserForm = { name: '', email: '', password: '', role: 'operator', tenantId: '' }
const emptyEdit: EditForm   = { name: '', role: 'operator', active: true }
const emptyPwd: PwdForm     = { newPassword: '' }

const roleLabel: Record<string, string> = { admin: 'Admin', operator: 'Operador', agent: 'Agente', client: 'Cliente' }
const roleVariant: Record<string, 'default' | 'info' | 'warning'> = { admin: 'warning', operator: 'info', agent: 'default', client: 'default' }

export function UsersPage() {
  const qc = useQueryClient()
  const [createModal, setCreateModal] = useState(false)
  const [editModal,   setEditModal]   = useState<User | null>(null)
  const [pwdModal,    setPwdModal]    = useState<User | null>(null)
  const [cForm, setCForm] = useState<UserForm>(emptyCreate)
  const [eForm, setEForm] = useState<EditForm>(emptyEdit)
  const [pForm, setPForm] = useState<PwdForm>(emptyPwd)

  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/users')).data,
  })

  const { data: clients = [] } = useQuery<Tenant[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
  })

  const openCreate = () => { setCForm(emptyCreate); setCreateModal(true) }
  const openEdit = (u: User) => { setEForm({ name: u.name, role: u.role, active: u.active }); setEditModal(u) }
  const openPwd = (u: User) => { setPForm(emptyPwd); setPwdModal(u) }

  const create = useMutation({
    mutationFn: async () => api.post('/users', { ...cForm, tenantId: cForm.tenantId || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setCreateModal(false) },
  })

  const edit = useMutation({
    mutationFn: async () => api.patch(`/users/${editModal!.id}`, eForm),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['users'] }); setEditModal(null) },
  })

  const changePwd = useMutation({
    mutationFn: async () => api.post(`/users/${pwdModal!.id}/change-password`, pForm),
    onSuccess: () => setPwdModal(null),
  })

  const fc = (k: keyof UserForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setCForm(p => ({ ...p, [k]: e.target.value }))
  const fe = (k: keyof EditForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setEForm(p => ({ ...p, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }))
  const fp = (k: keyof PwdForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setPForm(p => ({ ...p, [k]: e.target.value }))

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Usuários</h2>
          <p className="text-sm text-gray-500">{users.length} usuário(s) cadastrado(s)</p>
        </div>
        <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4" /> Novo usuário</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : users.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum usuário cadastrado</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Nome</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Email</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Perfil</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((u) => {
                    const clientName = u.tenantId ? clients.find(c => c.id === u.tenantId)?.name ?? '—' : '—'
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-900">{u.name}</td>
                        <td className="px-5 py-3 text-gray-600">{u.email}</td>
                        <td className="px-5 py-3">
                          <Badge variant={roleVariant[u.role] ?? 'default'}>{roleLabel[u.role] ?? u.role}</Badge>
                        </td>
                        <td className="px-5 py-3 text-gray-600">{clientName}</td>
                        <td className="px-5 py-3">
                          <Badge variant={u.active ? 'success' : 'danger'}>{u.active ? 'Ativo' : 'Inativo'}</Badge>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => openPwd(u)}><KeyRound className="h-4 w-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Criar usuário */}
      <Modal title="Novo usuário" open={createModal} onClose={() => setCreateModal(false)}>
        <div className="space-y-4">
          <Input label="Nome *" value={cForm.name} onChange={fc('name')} />
          <Input label="Email *" type="email" value={cForm.email} onChange={fc('email')} />
          <Input label="Senha * (mín. 8 caracteres)" type="password" value={cForm.password} onChange={fc('password')} />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Perfil</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={cForm.role} onChange={fc('role')}>
                <option value="admin">Admin</option>
                <option value="operator">Operador</option>
                <option value="agent">Agente (operacional)</option>
                <option value="client">Cliente</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Cliente (opcional)</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={cForm.tenantId} onChange={fc('tenantId')}>
                <option value="">Nenhum (admin global)</option>
                {clients.filter(c => c.active).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          {create.error && <p className="text-sm text-red-600">{
            (create.error as any)?.response?.status === 409
              ? 'Este e-mail já está cadastrado. Edite o usuário existente ou use outro e-mail.'
              : (create.error as any)?.response?.data?.detail ?? (create.error as any)?.response?.data?.title ?? 'Erro ao criar'
          }</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setCreateModal(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!cForm.name || !cForm.email || cForm.password.length < 8} onClick={() => create.mutate()}>
              Criar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Editar usuário */}
      <Modal title="Editar usuário" open={!!editModal} onClose={() => setEditModal(null)}>
        <div className="space-y-4">
          <Input label="Nome *" value={eForm.name} onChange={fe('name')} />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Perfil</label>
            <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" value={eForm.role} onChange={fe('role')}>
              <option value="admin">Admin</option>
              <option value="operator">Operador</option>
              <option value="agent">Agente (operacional)</option>
              <option value="client">Cliente</option>
            </select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" className="h-4 w-4 rounded" checked={eForm.active}
              onChange={e => setEForm(p => ({ ...p, active: e.target.checked }))} />
            <span className="text-sm text-gray-700">Ativo</span>
          </label>
          {edit.error && <p className="text-sm text-red-600">{(edit.error as any)?.response?.data?.detail ?? (edit.error as any)?.response?.data?.title ?? 'Erro ao salvar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setEditModal(null)}>Cancelar</Button>
            <Button loading={edit.isPending} disabled={!eForm.name} onClick={() => edit.mutate()}>Salvar</Button>
          </div>
        </div>
      </Modal>

      {/* Trocar senha */}
      <Modal title={`Trocar senha — ${pwdModal?.name}`} open={!!pwdModal} onClose={() => setPwdModal(null)} size="sm">
        <div className="space-y-4">
          <Input label="Nova senha (mín. 8 caracteres)" type="password" value={pForm.newPassword} onChange={fp('newPassword')} />
          {changePwd.error && <p className="text-sm text-red-600">{(changePwd.error as any)?.response?.data?.detail ?? (changePwd.error as any)?.response?.data?.title ?? 'Erro'}</p>}
          {changePwd.isSuccess && <p className="text-sm text-green-600">Senha alterada com sucesso!</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setPwdModal(null)}>Cancelar</Button>
            <Button loading={changePwd.isPending} disabled={pForm.newPassword.length < 8} onClick={() => changePwd.mutate()}>
              Alterar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
